import type { NavigationProp, ParamListBase } from "@react-navigation/native";
import { Formik } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

import { Routes } from "../../constants";
import { FeatureFlag, isFeatureFlagActive } from "../../helpers/featureFlags";
import { isPlantBasedDiet } from "../../helpers/userHelpers";
import type { DietEnum as DietaryPreference } from "../../services/backendTypes";
import { DietPreferences, onboardingDataSelector, onboardingSlice } from "../../slices/onboardingSlice";
import { userSelector } from "../../slices/userSlice";
import { DietaryPreferenceEnum } from "../../types";
import SelectionScreen, { Option } from "./SelectionScreen";

interface DietOption extends Option {
  value: DietaryPreference;
}

interface DietsScreenProps {
  navigation: NavigationProp<ParamListBase>;
}

const DietSelectionScreen: React.FC<DietsScreenProps> = ({ navigation }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const user = useSelector(userSelector);
  const onboardingData = useSelector(onboardingDataSelector);

  if (!user) {
    throw new Error("User is not defined");
  }

  const isPlantBasedApp = isFeatureFlagActive(user, FeatureFlag.FF_PLANT_BASED_APP);

  const dietOptions: DietOption[] = [
    // NOTE: Omnivore is the "default" option
    {
      label: t("general.diets.VEGAN"),
      description: "",
      value: DietaryPreferenceEnum.VEGAN,
      icon: "🌱", // Seedling
      testID: `diet-option-${DietaryPreferenceEnum.VEGAN}`,
    },
    // NOTE: We do not show these because they are confusing to users and are not used
    // I also note that lacto-vegetarian can be selected by selecting vegetarian and specifying a lactose intolerance
    // {
    //   label: t("general.diets.LACTO_VEGETARIAN"),
    //   description: "",
    //   value: "LACTO_VEGETARIAN",
    //   icon: "🥛", // Milk
    // },
    // {
    //   label: t("general.diets.OVO_VEGETARIAN"),
    //   description: "",
    //   value: "OVO_VEGETARIAN",
    //   icon: "🥚", // Egg
    // },
    {
      label: t("general.diets.VEGETARIAN"),
      description: "",
      value: DietaryPreferenceEnum.VEGETARIAN,
      icon: "🥗", // Green salad
      testID: `diet-option-${DietaryPreferenceEnum.VEGETARIAN}`,
    },
    {
      label: t("general.diets.PESCATARIAN"),
      description: "",
      value: DietaryPreferenceEnum.PESCATARIAN,
      icon: "🐟", // Fish
      testID: `diet-option-${DietaryPreferenceEnum.PESCATARIAN}`,
    },
    // NOTE: This depends on manually tagging recipes as halal, and we have not done this very well
    // Users see lots of non-halal recipes and so I am removing this option for now
    // Hardly any users choose halal:
    // {'coach__email': 'info@trainmore.nl', 'halal_client_count': 148},
    // {'coach__email': 'andrew+killerbody@weekmeals.co', 'halal_client_count': 71},
    // {'coach__email': 'info@senhuitgeverij.nl', 'halal_client_count': 48},
    // {'coach__email': 'martin_testing@test.com', 'halal_client_count': 12},
    // {'coach__email': 'abhi+bamifit@weekmeals.co', 'halal_client_count': 10},
    // {'coach__email': 'WHITELABEL+makkelijkafvallen@nutriaiplatform.com',
    //   'halal_client_count': 8},
    // {'coach__email': 'info@clubsportive.nl', 'halal_client_count': 7},
    // {'coach__email': 'abhi+lisa@weekmeals.co', 'halal_client_count': 5},
    // {'coach__email': 'andrew+pimklement@weekmeals.co', 'halal_client_count': 1},
    // {'coach__email': 'abhi+oanh@weekmeals.co', 'halal_client_count': 1}
    // {
    //   label: t("general.diets.HALAL"),
    //   description: "",
    //   value: DietaryPreferenceEnum.HALAL,
    //   icon: "🕌", // Mosque
    //   testID: `diet-option-${DietaryPreferenceEnum.HALAL}`,
    // },
  ];

  // Filter diet options if meat-free app is enabled
  const filteredDietOptions = isPlantBasedApp
    ? dietOptions.filter((option) => isPlantBasedDiet(option.value))
    : dietOptions;

  const validationSchema = Yup.object().shape({
    selectedDiet: Yup.mixed()
      .oneOf([...filteredDietOptions.map((option) => option.value)])
      [
        // Make selection required if meat-free app is enabled
        isPlantBasedApp ? "required" : "notRequired"
      ](isPlantBasedApp ? t("general.required") : undefined),
  });

  type FormSchema = Yup.InferType<typeof validationSchema>;

  const onSubmit = (values: FormSchema): void => {
    const updatedDietPreferences: DietPreferences = {
      ...onboardingData.dietPreferences,
      dietaryPreference: values.selectedDiet,
    };
    dispatch(onboardingSlice.actions.setDietPreferences(updatedDietPreferences));

    navigation.navigate(Routes.IntoleranceScreen);
  };

  return (
    <Formik
      initialValues={validationSchema.cast({ selectedDiet: undefined })}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ handleSubmit, values, setFieldValue, errors, touched }) => {
        const handleToggleSelection = (selectedOptions: string[]): void => {
          if (values.selectedDiet === selectedOptions[0]) {
            setFieldValue("selectedDiet", undefined);
          } else {
            setFieldValue("selectedDiet", selectedOptions[0] || null);
          }
        };

        return (
          <SelectionScreen
            title={
              isPlantBasedApp
                ? t("onboarding.onboarding_8b_diets_screen.title_plant_based_app")
                : t("onboarding.onboarding_8b_diets_screen.title")
            }
            subtitle={!isPlantBasedApp ? t("onboarding.onboarding_8b_diets_screen.subtitle") : ""}
            options={filteredDietOptions}
            selectedOptions={values.selectedDiet ? [values.selectedDiet] : []}
            setSelectedOptions={handleToggleSelection}
            onPressNext={handleSubmit}
            multipleSelection={false}
            onPressBack={() => navigation.goBack()}
            errorMessage={typeof errors.selectedDiet === "string" && touched.selectedDiet ? errors.selectedDiet : ""}
          />
        );
      }}
    </Formik>
  );
};

export default DietSelectionScreen;
